// src/amPlanner/index.js  →  dist/amPlanner.js  (global: AmPlanner)
//
// AM Weekly Planner (#8) — the area-manager version of a rota, in the managers
// app. Renders a Mon–Sun grid: one row per area manager, one editable free-text
// cell per day ("Office AM / Dubai Hills PM", "HOL", "D/O", "Induction", store
// combos). Reads/writes /api/admin/am-planner (backend = PR A). Click a cell to
// edit; blur or Ctrl/Cmd+Enter saves; clearing it removes the entry.
//
// Auth = the admin Bearer (DB.getToken), absolute Worker URL (DB.workerUrl) —
// relative /api/* would hit Netlify and 404. Read/upsert only; never pay/shifts.

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function esc(s) {
  return (typeof window !== 'undefined' && window.escapeHtml)
    ? window.escapeHtml(s)
    : String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// ── pure date helpers (UTC-safe; no naive TZ drift) ──
function pad(n) { return String(n).padStart(2, '0'); }
function parse(dateStr) { const [y, m, d] = String(dateStr).split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)); }
function fmt(dt) { return dt.getUTCFullYear() + '-' + pad(dt.getUTCMonth() + 1) + '-' + pad(dt.getUTCDate()); }
export function addDaysStr(dateStr, n) { const dt = parse(dateStr); dt.setUTCDate(dt.getUTCDate() + n); return fmt(dt); }
function dubaiTodayStr() { return new Date(Date.now() + 4 * 3600 * 1000).toISOString().slice(0, 10); }

export function dayHeader(dateStr, i) {
  const dt = parse(dateStr);
  return DAY_NAMES[i] + ' ' + dt.getUTCDate() + ' ' + MONTHS[dt.getUTCMonth()];
}
export function weekCommencingLabel(weekStart) {
  const dt = parse(weekStart);
  return 'Week commencing Mon ' + dt.getUTCDate() + ' ' + MONTHS[dt.getUTCMonth()] + ' ' + dt.getUTCFullYear();
}

// Pure: index cells by account → date → text, for O(1) grid lookup.
export function pivot(cells) {
  const by = {};
  (Array.isArray(cells) ? cells : []).forEach((c) => {
    const k = String(c.account_id);
    (by[k] = by[k] || {})[String(c.plan_date).slice(0, 10)] = c.plan_text || '';
  });
  return by;
}

// ── module state ──
const state = { week: null, data: null, loading: false, error: null };

function api(path, method, body) {
  const token = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  const base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = 'Bearer ' + token;
  return fetch(base + path, { cache: 'no-store', method: method || 'GET', headers, body: body ? JSON.stringify(body) : undefined });
}

function root() { return document.getElementById('amPlannerRoot'); }

export async function render(week) {
  const el = root();
  if (!el) return;
  renderBriefing(); // v127.8 (K3): fire-and-forget; cached, so week nav is free
  state.loading = true; state.error = null;
  el.innerHTML = '<div style="padding:24px;color:var(--tiny)">Loading the planner…</div>';
  const wk = week || state.week || dubaiTodayStr();
  try {
    const res = await api('/api/admin/am-planner?week=' + encodeURIComponent(wk));
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) throw new Error('Your session looks expired — sign out and back in.');
      throw new Error('Could not load the planner (' + res.status + ').');
    }
    state.data = await res.json();
    state.week = state.data.week_start;
  } catch (e) {
    state.error = (e && e.message) || String(e);
  } finally {
    state.loading = false;
  }
  draw();
}

export function go(deltaWeeks) { render(addDaysStr(state.week || dubaiTodayStr(), (deltaWeeks || 0) * 7)); }
export function today() { render(dubaiTodayStr()); }

function draw() {
  const el = root();
  if (!el) return;
  if (state.error) {
    el.innerHTML = '<div style="padding:20px;border:1px solid var(--arota-bad-border);background:var(--arota-bad-pale);border-radius:10px;color:var(--arota-bad-ink)">' +
      esc(state.error) + '</div>';
    return;
  }
  const d = state.data || {};
  const days = d.days || [];
  const managers = d.managers || [];
  const cellMap = pivot(d.cells);

  const nav =
    '<div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:14px">' +
      '<button class="btn btn-outline" onclick="AmPlanner.go(-1)">◀ Prev</button>' +
      '<button class="btn btn-outline" onclick="AmPlanner.today()">This week</button>' +
      '<button class="btn btn-outline" onclick="AmPlanner.go(1)">Next ▶</button>' +
      '<div style="font-weight:700;margin-inline-start:8px">' + esc(weekCommencingLabel(d.week_start)) + '</div>' +
    '</div>';

  if (!managers.length) {
    el.innerHTML = nav + '<div style="padding:20px;color:var(--tiny);border:1px dashed var(--border-strong);border-radius:10px">' +
      'No area managers found. Add accounts with the role <b>area_manager</b>, then they appear here as rows.</div>';
    return;
  }

  const headCells = days.map((dt, i) =>
    '<th style="padding:8px 10px;font-size:12px;font-weight:700;border-bottom:2px solid var(--border);white-space:nowrap">' +
    esc(dayHeader(dt, i)) + '</th>').join('');
  const head = '<tr><th style="padding:8px 10px;text-align:start;border-bottom:2px solid var(--border);position:sticky;inset-inline-start:0;background:var(--card)">Area manager</th>' + headCells + '</tr>';

  const rows = managers.map((mgr) => {
    const cells = days.map((dt) => {
      const txt = (cellMap[mgr.account_id] && cellMap[mgr.account_id][dt]) || '';
      return '<td class="amp-cell" data-acct="' + esc(mgr.account_id) + '" data-date="' + esc(dt) + '" ' +
        'contenteditable="true" ' +
        'style="padding:7px 9px;border:1px solid var(--border);vertical-align:top;min-width:120px;font-size:13px;line-height:1.35;white-space:pre-wrap;cursor:text">' +
        esc(txt) + '</td>';
    }).join('');
    const sub = mgr.area ? '<div style="font-size:11px;color:var(--tiny);font-weight:400">' + esc(mgr.area) + '</div>' : '';
    return '<tr><th style="padding:7px 10px;text-align:start;font-weight:700;border-bottom:1px solid var(--border);position:sticky;inset-inline-start:0;background:var(--card);white-space:nowrap">' +
      '<span translate="no">' + esc(mgr.name) + '</span>' + sub + '</th>' + cells + '</tr>';
  }).join('');

  el.innerHTML = nav +
    '<div style="overflow-x:auto;border:1px solid var(--border);border-radius:10px">' +
      '<table style="border-collapse:collapse;width:100%;background:var(--card)">' +
        '<thead>' + head + '</thead><tbody>' + rows + '</tbody>' +
      '</table>' +
    '</div>' +
    '<div style="margin-top:10px;font-size:12px;color:var(--tiny)">Click a cell to edit · blur or Ctrl/Cmd+Enter saves · clear a cell to remove the entry. ' +
      'Free text — e.g. Office AM / Dubai Hills PM, HOL, D/O, Induction.</div>' +
    '<div id="ampStatus" style="margin-top:6px;font-size:12px;height:16px"></div>';

  attachHandlers();
}

function status(msg, ok) {
  const s = document.getElementById('ampStatus');
  if (s) { s.textContent = msg || ''; s.style.color = ok === false ? 'var(--arota-bad-ink)' : 'var(--arota-good-ink)'; }
}

function attachHandlers() {
  const el = root();
  if (!el) return;
  el.querySelectorAll('.amp-cell').forEach((cell) => {
    // Remember the value on focus so we only save real changes.
    cell.addEventListener('focus', () => { cell.dataset.orig = cell.innerText; });
    cell.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' && (ev.ctrlKey || ev.metaKey)) { ev.preventDefault(); cell.blur(); }
    });
    cell.addEventListener('blur', () => {
      const next = cell.innerText.replace(/ /g, ' ').trim();
      const prev = (cell.dataset.orig || '').replace(/ /g, ' ').trim();
      if (next === prev) return;
      saveCell(cell, next);
    });
  });
}

async function saveCell(cell, text) {
  const account_id = cell.dataset.acct;
  const plan_date = cell.dataset.date;
  status('Saving…', true);
  try {
    const editor = (function () {
      try { return (window.currentUser && (window.currentUser.name || window.currentUser.email)) || null; } catch (e) { return null; }
    })();
    const res = await api('/api/admin/am-planner', 'POST', { account_id, plan_date, plan_text: text, updated_by: editor });
    if (!res.ok) throw new Error('save failed (' + res.status + ')');
    status('Saved.', true);
    setTimeout(() => status(''), 1200);
  } catch (e) {
    status('Could not save — ' + ((e && e.message) || e) + '. Your edit is still on screen; try again.', false);
  }
}

// ── "Who to talk to" briefing card (v127.8, dev-tab review K3) ───────────────
// The per-AM compliance briefing, moved here from the dev console so the
// managers app owns it. Reads GET /api/admin/am-briefing (same compliance scan
// as the Audit views); the Monday email is default-OFF — Preview emails
// EVERYTHING to the signed-in HO admin only, Send now emails each AM their own
// section with the admins cc'd.

const briefState = { data: null, loading: false };

function briefRoot() { return document.getElementById('amBriefingCard'); }

export async function renderBriefing(force) {
  const el = briefRoot();
  if (!el) return;
  if (briefState.data && !force) { drawBriefing(); return; }
  if (briefState.loading) return;
  briefState.loading = true;
  el.innerHTML = '<div style="padding:14px;color:var(--tiny);font-size:13px">Scanning compliance for the briefing…</div>';
  try {
    const res = await api('/api/admin/am-briefing');
    if (!res.ok) throw new Error('load failed (' + res.status + ')');
    briefState.data = await res.json();
  } catch (e) {
    briefState.data = { ok: false, error: (e && e.message) || String(e) };
  } finally {
    briefState.loading = false;
  }
  drawBriefing();
}

function drawBriefing() {
  const el = briefRoot();
  if (!el) return;
  const d = briefState.data || {};
  if (!d.ok) {
    el.innerHTML = '<div style="padding:12px 14px;border:1px solid var(--arota-warn-border);background:var(--arota-amber-pale);border-radius:10px;font-size:13px;color:var(--warn-deep)">' +
      'Could not load the Who-to-talk-to briefing — ' + esc(d.error || 'unknown') +
      ' <button class="btn btn-outline" style="margin-inline-start:8px" onclick="AmPlanner.renderBriefing(true)">Retry</button></div>';
    return;
  }
  const briefing = Array.isArray(d.briefing) ? d.briefing : [];
  const p = d.period || {};
  // v127.18: each AM row expands into the per-store action plan (same data the
  // Monday email carries) — sales vs target, compliance counts, customer
  // feedback, and the suggested actions, worst store first.
  const storeLine = (d) => {
    const bits = [];
    if (d.sales && d.sales.target > 0) bits.push('Sales <b>' + d.sales.pct + '%</b>');
    if (d.total > 0) bits.push('<b>' + d.total + '</b> issue' + (d.total === 1 ? '' : 's'));
    if (d.feedback && d.feedback.count > 0) bits.push('⭐ <b>' + d.feedback.avg + '</b>/10' + (d.feedback.negatives ? ' · <span style="color:var(--arota-bad-ink)">' + d.feedback.negatives + ' unhappy</span>' : ''));
    // v127.19: the extra angles — demo/add-on % vs target, SVR recency.
    if (d.demo && d.demo.pct != null) bits.push('Demo <b>' + d.demo.pct + '%</b>');
    if (d.addons && d.addons.pct != null) bits.push('Add-ons <b>' + d.addons.pct + '%</b>');
    if (d.svr && d.svr.daysAgo != null) bits.push('SVR <b>' + d.svr.daysAgo + 'd</b>' + (d.svr.overdue ? ' ⚠' : ''));
    else if (d.svr && d.svr.overdue) bits.push('<span style="color:var(--ui-warn)">no SVR</span>');
    const badges = (d.badges && d.badges.length)
      ? '<div style="font-size:11.5px;color:var(--arota-good-ink-2);margin-top:2px">🏅 ' + esc(d.badges.join(' · ')) + '</div>' : '';
    const dot = d.score >= 10 ? '#dc2626' : d.score >= 4 ? '#d97706' : '#059669';
    return '<div style="padding-block:6px;padding-inline:12px 0;border-inline-start:3px solid ' + dot + ';margin:6px 0">' +
      '<div style="font-size:12.5px;font-weight:700"><span translate="no">' + esc(d.store) + '</span>' + (bits.length ? ' <span style="font-weight:400;color:var(--tiny)">— ' + bits.join(' · ') + '</span>' : '') + '</div>' +
      badges +
      '<ul style="margin:3px 0 0;padding-inline-start:16px;font-size:12px;color:var(--text)">' +
        (d.actions || []).map((a) => '<li>' + esc(a) + '</li>').join('') + '</ul>' +
      '</div>';
  };
  const rows = briefing.map((b) => {
    const sw = b.storeCount === 1 ? 'store' : 'stores';
    const details = (b.storeDetails && b.storeDetails.length)
      ? '<details style="margin-top:4px"><summary style="cursor:pointer;font-size:12px;color:var(--arota-link)">Store-by-store action plan</summary>' +
        b.storeDetails.map(storeLine).join('') + '</details>'
      : '<div style="font-size:11px;color:var(--tiny);margin-top:2px" translate="no">' + esc((b.stores || []).join(', ')) + '</div>';
    return '<div style="display:flex;gap:12px;padding:10px 0;border-top:1px solid var(--border)">' +
      '<div style="min-width:130px;font-weight:800" translate="no">' + esc(b.am) + '</div>' +
      '<div style="flex:1;min-width:0">' +
        '<div style="font-size:13px"><b>' + b.total + '</b> issue' + (b.total === 1 ? '' : 's') +
          ' · <b>' + b.storeCount + '</b> ' + sw + '</div>' +
        '<div style="font-size:12px;color:var(--tiny);margin-top:2px">' + esc((b.summary || []).join(' · ')) + '</div>' +
        details +
      '</div></div>';
  }).join('');
  const last = d.last_attempt;
  const lastLine = last
    ? 'Last email attempt: ' + esc(String(last.ts || '').slice(0, 16).replace('T', ' ')) + ' — ' + esc(last.outcome || '?') +
      (last.detail ? ' (' + esc(last.detail) + ')' : '') + (last.via ? ' · via ' + esc(last.via) : '')
    : 'No email attempts recorded yet.';
  const toggleOn = !!d.live_enabled;
  const controls =
    '<div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-top:10px;padding-top:10px;border-top:1px solid var(--border)">' +
      '<label style="display:flex;align-items:center;gap:6px;font-size:12px;cursor:pointer">' +
        '<input type="checkbox" ' + (toggleOn ? 'checked ' : '') + 'onchange="AmPlanner.briefingToggle(this.checked)">' +
        'Email each area manager every Monday (admins cc\'d)' +
      '</label>' +
      '<button class="btn btn-outline" onclick="AmPlanner.briefingPreview()">📧 Send preview to me</button>' +
      '<button class="btn btn-outline" onclick="AmPlanner.briefingSendNow()">📤 Send to AMs now</button>' +
      '<button class="btn btn-outline" onclick="AmPlanner.renderBriefing(true)">🔄 Refresh</button>' +
      '<span id="amBriefStatus" style="font-size:12px"></span>' +
    '</div>' +
    '<div style="font-size:11px;color:var(--tiny);margin-top:6px">' + lastLine +
      (d.resend_configured ? '' : ' · Email is not configured on this environment (no Resend key) — the card still works, sends are disabled.') +
    '</div>';
  el.innerHTML =
    '<div style="border:1px solid var(--border);border-radius:10px;background:var(--card);padding:14px 16px">' +
      '<div style="font-weight:800;font-size:15px">🧭 Who to talk to — last ' + (p.days || 7) + ' days</div>' +
      '<div style="font-size:12px;color:var(--tiny);margin-top:2px">Compliance &amp; attendance issues grouped by area manager (' + esc(p.from || '') + ' → ' + esc(p.to || '') + ').</div>' +
      (rows || '<div style="padding:10px 0;font-size:13px;color:var(--arota-good-ink)">No compliance or attendance issues in this period. 🎉</div>') +
      controls +
    '</div>';
}

function briefStatus(msg, ok) {
  const s = document.getElementById('amBriefStatus');
  if (s) { s.textContent = msg || ''; s.style.color = ok === false ? 'var(--arota-bad-ink)' : 'var(--arota-good-ink)'; }
}

export async function briefingToggle(enabled) {
  briefStatus('Saving…', true);
  try {
    const res = await api('/api/admin/am-briefing/toggle', 'POST', { enabled: !!enabled });
    if (!res.ok) throw new Error(res.status === 403 ? 'Head Office only' : 'save failed (' + res.status + ')');
    if (briefState.data) briefState.data.live_enabled = !!enabled;
    briefStatus(enabled ? 'Monday send is ON.' : 'Monday send is OFF.', true);
  } catch (e) {
    briefStatus('Could not save — ' + ((e && e.message) || e), false);
    renderBriefing(true); // re-sync the checkbox with the server truth
  }
}

export async function briefingPreview() {
  briefStatus('Sending the preview to your email…', true);
  try {
    const res = await api('/api/admin/am-briefing/preview', 'POST', {});
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || (res.status === 403 ? 'Head Office only' : 'send failed (' + res.status + ')'));
    briefStatus('Preview sent to your email — nobody else.', true);
  } catch (e) {
    briefStatus('Preview failed — ' + ((e && e.message) || e), false);
  }
}

export async function briefingSendNow() {
  if (!window.confirm('Email each area manager their briefing NOW (admins cc\'d)? This sends real emails.')) return;
  briefStatus('Sending to the area managers…', true);
  try {
    const res = await api('/api/admin/am-briefing/send-now', 'POST', {});
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || (res.status === 403 ? 'Head Office only' : 'send failed (' + res.status + ')'));
    const skipped = Array.isArray(body.skipped_ams) && body.skipped_ams.length ? ' (no account email for: ' + body.skipped_ams.join(', ') + ')' : '';
    briefStatus(body.skipped ? ('Nothing sent — ' + (body.reason || 'nothing to send')) : ('Sent ' + (body.sent || 0) + ' email' + (body.sent === 1 ? '' : 's') + '.' + skipped), true);
    renderBriefing(true);
  } catch (e) {
    briefStatus('Send failed — ' + ((e && e.message) || e), false);
  }
}
